import { formatISO } from 'date-fns/esm'
import { mapValues } from 'lodash-es'
import ExtendedMap from 'src/models/ExtendedMap'

import lessonClassApi from '@/api/lessonClass.api'
import smsApi from '@/api/sms.api'
import studentApi from '@/api/student.api'
import type { SmsPost, Student } from '@/dto'
import {
  Sms,
  SmsCollection,
  SmsStudent,
  SmsStudentGroup,
  SmsStudentGroupColl,
  SmsSummary,
  SmsTemplate,
} from '@/dto'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

export class SmsController implements Controller {
  adapter = {
    sms: {
      from(data: res.Sms): Sms {
        return new Sms(data)
      },
      fromSummary(data: { success: number; fail: number; requested: number }): SmsSummary {
        return new SmsSummary(data)
      },
      to(
        studentIdList: req.StudentId,
        recipient: SmsStudentGroupColl['recipient'],
      ): req.SmsRecipient {
        const target = recipient === '학부모' ? 'PARENTS' : 'STUDENT'
        return { studentIdList, target }
      },
      toSend({ sendingTarget, studentIdList, content }: SmsPost): req.SmsSend {
        return { sendingTarget, studentIdList, content }
      },
      toDate(data: { startDatetime: Date; endDatetime: Date }): req.SmsByDate {
        return mapValues(data, (dateTime) => formatISO(dateTime))
      },
    },
    template: makeAdapter({
      from(data: res.SmsTemplate): SmsTemplate {
        return new SmsTemplate(data)
      },
      to(data: SmsTemplate): req.SmsTemplate {
        return { title: data.title, content: data.content }
      },
      toIds(data: SmsTemplate['id']): req.SmsTemplateIds {
        return [data]
      },
    }),
    studentGroup: makeAdapter({
      from(data: res.StudentIncludePhone | res.LessonClassesIncludePhone) {
        const memberList = data.memberList.map((member) => new SmsStudent(member))
        return new SmsStudentGroup({
          ...data,
          memberList,
        })
      },
    }),
  }

  fetchSmsStudents = async () => {
    const [gradeStudentGroups, classStudentGroups] = await Promise.all([
      this.fetchGradeSmsStudents(),
      this.fetchClassSmsStudents(),
    ])
    return new SmsStudentGroupColl({ gradeStudentGroups, classStudentGroups })
  }

  private fetchGradeSmsStudents = async () => {
    const {
      data: { data },
    } = await studentApi.getStudentPhone()
    return data.map(this.adapter.studentGroup.from)
  }

  private fetchClassSmsStudents = async () => {
    const {
      data: {
        data: { content },
      },
    } = await lessonClassApi.getLessonClassesPhone()

    return content.map(this.adapter.studentGroup.from)
  }

  fetchSmsById = async (
    id: req.StudentId,
    recipient: SmsStudentGroupColl['recipient'],
    pageQueryString: string,
  ) => {
    const {
      data: {
        data: {
          page: { content, last },
        },
      },
    } = await smsApi.getSmsById(this.adapter.sms.to(id, recipient), pageQueryString)
    const smsArr = content.map(this.adapter.sms.from)
    return { smsColl: new SmsCollection(ExtendedMap.fromArr(smsArr, 'id')), last }
  }

  fetchSmsUsage = async (
    params: { startDatetime: Date; endDatetime: Date },
    pageQueryString: string,
  ) => {
    const {
      data: {
        data: {
          page: { content, last },
          summary,
        },
      },
    } = await smsApi.getSmsByDate(this.adapter.sms.toDate(params), pageQueryString)
    const smsArr = content.map(this.adapter.sms.from)
    return {
      collection: new SmsCollection(ExtendedMap.fromArr(smsArr, 'id')),
      summary: this.adapter.sms.fromSummary(summary),
      last,
    }
  }

  fetchSmsTemplate = async (pageQueryString: string) => {
    const {
      data: {
        data: { content },
      },
    } = await smsApi.getSmsTemplate(pageQueryString)

    return content.map(this.adapter.template.from)
  }

  fetchSmsBasicTemplateBySendingType = async (
    templateSendingType: res.SmsTemplate['templateSendingType'],
  ) => {
    const { data } = await smsApi.getSmsBasicTemplateBySendingType(templateSendingType)

    return this.adapter.template.from(data)
  }

  deleteSmsTemplateById = async (id: SmsTemplate['id']) => {
    await smsApi.deleteSmsTemplateByIds(this.adapter.template.toIds(id))
  }

  createSmsTemplate = async (template: SmsTemplate) => {
    await smsApi.postSmsTemplate(this.adapter.template.to(template))
  }

  patchSmsTemplate = async (template: SmsTemplate) => {
    await smsApi.patchSmsTemplate(template.id, this.adapter.template.to(template))
  }

  sendSms = async (sms: SmsPost) => {
    return smsApi.postSms(this.adapter.sms.toSend(sms))
  }

  fetchAttendanceSmsSample = async (params: req.AttendanceSmsSampleParams) => {
    const {
      data: { data },
    } = await smsApi.getAttendanceSmsSample(params)

    return data.messages
  }

  sendIdGuideSmsToStudent = async (templateId, studentId: Student['id'][]) => {
    await smsApi.postIdGuideSms({
      templateId,
      sendingTarget: 'STUDENT',
      studentIdList: studentId,
    })
  }

  sendIdGuideSmsToParents = async (templateId, studentId: Student['id'][]) => {
    await smsApi.postIdGuideSms({
      templateId,
      sendingTarget: 'PARENTS',
      studentIdList: studentId,
    })
  }
}

export const smsController = new SmsController()
